import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, ArrowLeft, Truck, CalendarCheck, Users, BadgeDollarSign, CheckCircle2 } from 'lucide-react';
import { contactInfo } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const benefits = [
  {
    icon: BadgeDollarSign,
    title: 'Corporate Training Rates',
    description: 'Volume pricing for fleets training multiple drivers. The more drivers you send, the more you save.'
  },
  {
    icon: CalendarCheck,
    title: 'Priority Scheduling',
    description: 'Fleet partners get first access to class seats so your drivers are road-ready on your timeline.'
  },
  {
    icon: Users,
    title: 'Hire Our Graduates',
    description: 'Get direct access to our pipeline of newly licensed CDL graduates ready to fill your driver seats.'
  },
  {
    icon: Truck,
    title: 'CDL & HazMat Training',
    description: 'Class A, Class B, upgrades, and HazMat endorsements — all under one roof in Mukilteo, WA.'
  }
];

export default function Fleet() {
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    driversCount: '',
    message: '',
    website: ''
  });
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToForm = () => {
    document.getElementById('fleet-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.driversCount) {
      toast.error('Please tell us how many drivers need training.');
      return;
    }
    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA verification.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/fleet-leads`, {
        ...formData,
        recaptchaToken
      });
      toast.success(response.data.message);
      setFormData({ name: '', company: '', phone: '', email: '', driversCount: '', message: '', website: '' });
      setRecaptchaToken('');
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 min-w-0" data-testid="fleet-logo-home-link">
            <img
              src="https://customer-assets.emergentagent.com/job_pro-trucker-learn/artifacts/g2xcv5lg_Diamond-Trucking-Logo.jpg"
              alt="Diamond Trucking School"
              className="h-10 w-10 lg:h-12 lg:w-12 object-contain flex-shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-base lg:text-xl font-bold text-[#1e3a5f] truncate">Diamond Trucking School</h1>
              <p className="hidden sm:block text-sm italic font-bold text-gray-600 truncate">Fleet & Corporate Training</p>
            </div>
          </Link>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link to="/" className="hidden sm:flex items-center text-gray-700 hover:text-[#d97706] font-medium transition-colors" data-testid="fleet-back-home-link">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Home
            </Link>
            <a
              href="tel:4258902558"
              className="inline-flex items-center bg-[#1e3a5f] hover:bg-[#152940] text-white text-lg font-bold px-4 py-2 rounded-md transition-colors"
              data-testid="fleet-header-call-btn"
            >
              <Phone className="w-4 h-4 mr-2" />
              425.890.2558
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6b] text-white py-20 px-4">
        <div className="container mx-auto max-w-5xl text-center">
          <div className="inline-block bg-[#d97706] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
            For Fleet Managers & Trucking Companies
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Stop Losing Loads to <span className="text-[#d97706]">Driver Shortages</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-8">
            We train YOUR hires to CDL-ready in weeks — with corporate rates, priority scheduling, and direct access to our graduate pipeline for Washington State fleets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-[#d97706] hover:bg-[#b45309] text-white text-lg px-10 py-7 shadow-lg"
              data-testid="fleet-hero-cta-btn"
            >
              Get Fleet Pricing
            </Button>
            <Button
              onClick={() => window.location.href = `tel:${contactInfo.phone}`}
              size="lg"
              variant="outline"
              className="border-2 border-white bg-transparent text-white hover:bg-white hover:text-[#1e3a5f] text-lg px-10 py-7"
              data-testid="fleet-hero-call-btn"
            >
              <Phone className="mr-2 w-5 h-5" />
              Call: {contactInfo.phone}
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-[#f0f9ff]">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] text-center mb-12">
            Why WA Fleets Partner With Us
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <Card key={benefit.title} className="shadow-lg hover:shadow-xl transition-shadow border-t-4 border-[#d97706]">
                <CardContent className="pt-6 text-center">
                  <div className="w-14 h-14 bg-[#1e3a5f] rounded-lg flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-7 h-7 text-[#d97706]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1e3a5f] mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="fleet-form" className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
                Get Your Fleet Quote
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Tell us how many drivers you need trained and we'll respond within 1 business day with corporate rates and available class dates.
              </p>
              <ul className="space-y-4">
                {[
                  'No long-term contract required',
                  'Train 1 driver or your whole fleet',
                  'Flexible weekday & weekend schedules',
                  'Located in Mukilteo — serving all of Washington State'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#d97706] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-[#1e3a5f]">Fleet Training Inquiry</CardTitle>
                <CardDescription>We'll get back to you within 1 business day.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="Jane Smith"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="fleet-name-input"
                      className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company Name *</Label>
                    <Input
                      id="company"
                      name="company"
                      placeholder="Northwest Freight LLC"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      data-testid="fleet-company-input"
                      className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="fleet-phone-input"
                      className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jane@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="fleet-email-input"
                      className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="driversCount">How many drivers need training this year? *</Label>
                    <Select
                      value={formData.driversCount}
                      onValueChange={(value) => setFormData({ ...formData, driversCount: value })}
                    >
                      <SelectTrigger data-testid="fleet-drivers-select" className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]">
                        <SelectValue placeholder="Select an estimate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-2">1-2 drivers</SelectItem>
                        <SelectItem value="3-5">3-5 drivers</SelectItem>
                        <SelectItem value="6-10">6-10 drivers</SelectItem>
                        <SelectItem value="11+">11+ drivers</SelectItem>
                        <SelectItem value="not-sure">Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Anything else we should know?</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Timeline, license types needed, hiring goals..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      data-testid="fleet-message-input"
                      className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]"
                    />
                  </div>

                  {/* Honeypot field - hidden from real users */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.website}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="flex justify-center" data-testid="fleet-recaptcha-widget">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={(token) => setRecaptchaToken(token || '')}
                      onExpired={() => setRecaptchaToken('')}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="fleet-submit-btn"
                    className="w-full bg-[#d97706] hover:bg-[#b45309] text-white py-6 text-lg font-semibold"
                  >
                    {isSubmitting ? 'Sending...' : 'Request Fleet Pricing'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="bg-[#1e3a5f] text-white py-8 px-4 text-center">
        <p className="text-lg font-semibold mb-1">Diamond Trucking School — Fleet & Corporate Training</p>
        <p className="text-gray-300 text-sm mb-3">{contactInfo.address}</p>
        <a href={`tel:${contactInfo.phone}`} className="text-[#d97706] font-bold text-xl hover:underline" data-testid="fleet-footer-phone-link">
          {contactInfo.phone}
        </a>
      </footer>
    </div>
  );
}
